<template>
  <div>
    <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
      <form :class="isDisabled('sfmPositionId') ? 'viewmode' : ''">
        <div class="flex">
          <FormInputText
            v-model="areaDesignation"
            name="areaDesignation"
            :isDirty="handleDirtyState('areaDesignation')"
            :initialValue="editData?.areaDesignation"
            :disabled="isDisabled('areaDesignation')"
            :suggestions="props.suggestions?.areaDesignation"
          />
          <FormInputText
            v-model="nameOnStatement"
            name="nameOnStatement"
            :isDirty="handleDirtyState('nameOnStatement')"
            :initialValue="editData?.nameOnStatement"
            :disabled="isDisabled('nameOnStatement')"
            :suggestions="props.suggestions?.nameOnStatement"
          />
        </div>
        <div class="flex">
          <FormSelect
            v-model="sfmPositionId"
            name="sfmPositionId"
            label="sfmPosition"
            :options="sfmPositions"
            :filter="true"
            :isDirty="handleDirtyState('sfmPositionId')"
            :initialValue="editData?.sfmPositionId"
            :disabled="isDisabled('sfmPositionId')"
          />
          <FormSelect
            v-model="unit"
            name="unit"
            :options="unitOptions"
            :untranslated="true"
            :isDirty="handleDirtyState('unit')"
            :initialValue="editData?.unit"
            :disabled="isDisabled('unit')"
          />
        </div>
        <div class="flex">
          <FormInputNumber
            v-model="totalArea"
            name="totalUnit"
            :isDirty="handleDirtyState('totalArea')"
            :initialValue="editData?.totalArea"
            :minFractionDigits="2"
            :suffix="unitSuffix"
            :disabled="isDisabled('totalArea')"
          />
          <FormInputNumber
            v-model="totalAmount"
            name="totalAmount"
            :isDirty="handleDirtyState('totalAmount')"
            :initialValue="editData?.totalAmount"
            formatType="currency"
            :currency="effectiveCurrency"
            :disabled="isDisabled('totalAmount')"
          />
        </div>
        <div class="flex">
          <FormInputNumber
            v-model="customerArea"
            name="customerUnit"
            :isDirty="handleDirtyState('customerArea')"
            :initialValue="editData?.customerArea"
            :minFractionDigits="2"
            :suffix="unitSuffix"
            :disabled="isDisabled('customerArea')"
          />
          <FormInputNumber
            v-model="customerAmount"
            :labelTooltip="`${$t('labels.customerAmountSuggestion')}: ${customerAmountSuggestion} ${effectiveCurrency}`"
            name="customerAmount"
            :isDirty="handleDirtyState('customerAmount')"
            :initialValue="editData?.customerAmount"
            formatType="currency"
            :currency="effectiveCurrency"
          />
        </div>

        <div class="flex">
          <FormCheckbox
            v-model="isFlatRate"
            name="isFlatRate"
            :isDirty="handleDirtyState('isFlatRate')"
            :initialValue="editData?.isFlatRate"
            :disabled="isDisabled('isFlatRate')"
          />
          <FormSelect
            v-model="status"
            name="status"
            :options="Object.values(OperatingCostInvoiceLineItemV1Dto.status)"
            :isDirty="handleDirtyState('status')"
            :initialValue="editData?.status"
            :disabled="isDisabled('status')"
          />
        </div>
        <DetailFormfooter
          :isNew="isCreate"
          :editMode="editMode"
          :isAnyFieldDirty="isAnyFieldDirty"
          :onGoToList="goToList"
          :onToggleEditMode="() => {}"
          :onSubmit="submitForm"
          :onConfirmResetChanges="confirmResetChanges"
          :onSaveAndCreateNext="submitFormForNextEntry"
        />
      </form>
    </SectionWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type {
  DetailFormDisableProps,
  DetailFormExtraProps,
} from '@/components/details/detailTypes';
import {
  DetailNavigationInlinePropsDefaults,
  useDetailNavigation,
  type UseDetailNavigationInlineProps,
} from '@/components/details/hooks/useDetailNavigation';
import type { SelectModel } from '@/components/forms/FormSelect.vue';
import { useFormDirtyState } from '@/components/forms/hooks/useFormDirtyState';
import { useFieldDisabling } from '@/components/forms/hooks/useFormFieldDisable';
import { useSfmPositions } from '@/views/sfmpositions/hooks/useSfmPositions';
import {
  CreateOperatingCostInvoiceLineItemV1Dto,
  OperatingCostInvoiceLineItemV1Dto,
} from '@/openapi';

import {
  getOperatingCostInvoiceLineSchema,
  UNIT_SUFFIXES,
} from './configs/operatingCostInvoiceLineFormConfig';
import {
  useOperatingCostInvoiceLines,
  useOperatingCostInvoiceLinesConverter,
} from './hooks/useOperatingCostInvoiceLines';
import type { OperatingCostInvoiceLineItemSimple } from './operatingCostInvoiceTypes';

export interface CostInvoiceLineFormProps {
  isNew: boolean;
  isCreate: boolean;
  formData: OperatingCostInvoiceLineItemSimple | undefined;
  currency: string;
  onAddCostInvoiceLineToCreate: (item: CreateOperatingCostInvoiceLineItemV1Dto) => void;
  onAddCostInvoiceLineToUpdate: (item: OperatingCostInvoiceLineItemSimple) => void;
  onModalClose: (reopenForNextEntry?: boolean) => void | Promise<void>;
  daysInBillingPeriod?: number;
}

const props = withDefaults(
  defineProps<
    UseDetailNavigationInlineProps &
      CostInvoiceLineFormProps &
      DetailFormExtraProps<OperatingCostInvoiceLineItemSimple> &
      DetailFormDisableProps<OperatingCostInvoiceLineItemSimple>
  >(),
  {
    ...DetailNavigationInlinePropsDefaults,
    initialData: undefined,
    placeholders: undefined,
    allDisabled: undefined,
    disabledFields: undefined,
    suggestions: undefined,
    daysInBillingPeriod: undefined,
  },
);

const { t } = useI18n();

const { id, goToList, subComponentID } = useDetailNavigation({
  uuidFromProp: computed(() => props.uuid), // override id
  isNewFromProp: computed(() => props.isNew), // override isNew
  editModeFromProp: computed(() => props.editMode), // override editMode
});

const { isAnyFieldDirty, handleDirtyState } = useFormDirtyState();

const lineEditMode = computed(() => true); // Line form is always in edit mode inside modal

const { isDisabled } = useFieldDisabling<OperatingCostInvoiceLineItemSimple>({
  editMode: lineEditMode,
  allDisabled: computed(() => props.allDisabled),
  disabledFields: computed(() => props.disabledFields),
});

// Ensure currency always has a value (fallback to EUR if not provided)
const effectiveCurrency = computed(() => props.currency || 'EUR');

const { createOperatingCostInvoiceLine, updateOperatingCostInvoiceLine } =
  useOperatingCostInvoiceLines();
const {
  convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto,
  convertOperatingCostInvoiceLineItemSimpleToPatchOperatingCostInvoiceLineItemDto,
} = useOperatingCostInvoiceLinesConverter();
const { fetchSfmPositions } = useSfmPositions();

const editData = ref<OperatingCostInvoiceLineItemSimple>();
const sfmPositions = ref<SelectModel[]>([]);

const { handleSubmit, setValues } = useForm<OperatingCostInvoiceLineItemSimple>({
  validationSchema: getOperatingCostInvoiceLineSchema(t),
});
const submitForm = handleSubmit((values) => onSubmit(values, false));
const submitFormForNextEntry = handleSubmit((values) => onSubmit(values, true));

const { value: areaDesignation } = useField<string>('areaDesignation');
const { value: nameOnStatement } = useField<string>('nameOnStatement');
const { value: isFlatRate } = useField<boolean>('isFlatRate');
const { value: status } = useField<string>('status');
const { value: totalAmount } = useField<number | undefined>('totalAmount');
const { value: customerAmount } = useField<number | undefined>('customerAmount');
const { value: totalArea } = useField<number | undefined>('totalArea');
const { value: customerArea } = useField<number | undefined>('customerArea');
const { value: sfmPositionId } = useField<string>('sfmPositionId');
const { value: unit } = useField<CreateOperatingCostInvoiceLineItemV1Dto.unit | undefined>('unit');

const unitOptions = Object.entries(UNIT_SUFFIXES).map(([id, name]) => ({ id, name }));
const unitSuffix = computed(() => (unit.value ? (UNIT_SUFFIXES[unit.value] ?? '') : ''));

const customerAmountSuggestion = ref<number | undefined>(undefined);

function confirmResetChanges() {
  setValues({ ...props.formData });
}

async function onSubmit(
  values: OperatingCostInvoiceLineItemSimple,
  reopenForNextEntry: boolean = false,
) {
  if (props.isNew) {
    if (props.isCreate) {
      props.onAddCostInvoiceLineToCreate(
        convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto(values),
      );
    } else {
      props.onAddCostInvoiceLineToUpdate(values);
    }
  } else {
    if (!props.isCreate) {
      await updateOperatingCostInvoiceLine(
        String(props.uuid ? id.value : subComponentID.value),
        values.id,
        convertOperatingCostInvoiceLineItemSimpleToPatchOperatingCostInvoiceLineItemDto(values),
      );
    } else {
      await createOperatingCostInvoiceLine(
        String(props.uuid ? id.value : subComponentID.value),
        convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto(values),
      );
    }
  }

  await props.onModalClose(reopenForNextEntry);
}

const fetchLookups = async () => {
  const positions = (await fetchSfmPositions()) ?? [];
  sfmPositions.value = positions
    .flatMap((parent) => {
      return (parent.children ?? []).map((child) => ({
        id: child.id,
        name: `${child.name} (${parent.name})`,
      }));
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

const calculateCustomerAmount = () => {
  if (
    totalAmount.value === undefined ||
    totalArea.value === undefined ||
    customerArea.value === undefined ||
    totalAmount.value <= 0 ||
    totalArea.value <= 0 ||
    !props.daysInBillingPeriod
  ) {
    return;
  }

  // Step 1: (Total Amount / Total Area) * Customer Area
  const customerAmountFromArea = (totalAmount.value / totalArea.value) * customerArea.value;

  // Step 2: (Customer Amount / 365) * Days in Billing Period

  const customerAmountPerYear =
    Math.round((customerAmountFromArea / 365) * props.daysInBillingPeriod * 100) / 100;

  customerAmountSuggestion.value = customerAmountPerYear;

  if (props.isCreate) {
    customerAmount.value = customerAmountPerYear;
  }
};

onMounted(async () => {
  await fetchLookups();

  if (props.initialData) {
    editData.value = {
      ...(props.formData ?? {}),
      ...props.initialData,
      status:
        props.formData?.status ||
        props.initialData.status ||
        OperatingCostInvoiceLineItemV1Dto.status.CLEAR,
    } as OperatingCostInvoiceLineItemSimple;
    setValues(editData.value);
    return;
  }

  // 2) Normal Edit-Mode or View-Mode: existing line with formData
  if (props.formData) {
    editData.value = props.formData;
    setValues({ ...props.formData });
  }
});

// Watch for changes in formData (e.g. when parent computed property updates)
watch(
  () => props.formData,
  (newFormData) => {
    if (newFormData && !props.initialData) {
      editData.value = newFormData;
      setValues({ ...newFormData });
    }
  },
  { deep: true, immediate: false },
);

// Watch for changes in totalArea, totalAmount, or customerArea to recalculate customer amount
watch([totalArea, totalAmount, customerArea], () => {
  calculateCustomerAmount();
});

// Watch for changes in days from parent
watch(
  () => props.daysInBillingPeriod,
  () => {
    calculateCustomerAmount();
  },
);
</script>
